/**
 * ReporteComponents.jsx
 * ─────────────────────────────────────────────────────────────────────
 * Componentes visuales e infraestructura compartida entre todos los
 * reportes generativos (Captación, Cartera Comercial, Consumo, Vivienda).
 *
 * Exports:
 *   Componentes: KpiCard, BarRow, SectionHeader, InsightBox,
 *                MapaEstados, MapaMunicipios, MapaLeyenda
 *   Helpers:     fmt, fmtB, fmtPct, fmtN, stripMarkdown
 *   GeoJSON:     fetchGeoEstados, fetchGeoMunicipios
 *   Datos:       normalizeStr, MX_ISO_TO_CVE, ALIAS_ESTADO
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { C as _C } from "./theme";

// ── Paleta ────────────────────────────────────────────────────────────
export const COLOR = {
  ..._C,
  primary:   _C.surface,
  lightBlue: _C.border,
  gray:      _C.muted,
  grayLight: _C.card,
};

// ── Helpers de formato ────────────────────────────────────────────────
export const fmt    = (n, dec = 1) => (n ?? 0).toFixed(dec);
export const fmtB   = n => `${fmt(n, 2)} mmdp`;
export const fmtPct = n => n == null ? "—" : `${n > 0 ? "+" : ""}${fmt(n, 1)}%`;
export const fmtN   = n => (n ?? 0).toLocaleString("es-MX");

/** Limpia markdown residual que el LLM puede incluir */
export function stripMarkdown(raw) {
  return (raw || "")
    .replace(/^#{1,3}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^---+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ── GeoJSON — singletons con cache ────────────────────────────────────
const GEOJSON_URL     = "https://raw.githubusercontent.com/angelnmara/geojson/master/mexicoHigh.json";
const GEOJSON_MUN_URL = "/municipios_mx.geojson";

let _cacheEstados    = null;
let _promEstados     = null;
let _cacheMunicipios = null;
let _promMunicipios  = null;

export function fetchGeoEstados() {
  if (_cacheEstados) return Promise.resolve(_cacheEstados);
  if (_promEstados)  return _promEstados;
  _promEstados = fetch(GEOJSON_URL).then(r => r.json())
    .then(d => { _cacheEstados = d; return d; });
  return _promEstados;
}

export function fetchGeoMunicipios() {
  if (_cacheMunicipios) return Promise.resolve(_cacheMunicipios);
  if (_promMunicipios)  return _promMunicipios;
  _promMunicipios = fetch(GEOJSON_MUN_URL).then(r => r.json())
    .then(d => { _cacheMunicipios = d; return d; });
  return _promMunicipios;
}

// ── Geo helpers ───────────────────────────────────────────────────────
export function normalizeStr(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z ]/g, "")
    .trim()
    .replace(/\s+/g, " ");
}

export const MX_ISO_TO_CVE = {
  "MX-AGU":1, "MX-BCN":2, "MX-BCS":3, "MX-CAM":4, "MX-COA":5,
  "MX-COL":6, "MX-CHP":7, "MX-CHH":8, "MX-CMX":9, "MX-DUR":10,
  "MX-GUA":11,"MX-GRO":12,"MX-HID":13,"MX-JAL":14,"MX-MEX":15,
  "MX-MIC":16,"MX-MOR":17,"MX-NAY":18,"MX-NLE":19,"MX-OAX":20,
  "MX-PUE":21,"MX-QUE":22,"MX-ROO":23,"MX-SLP":24,"MX-SIN":25,
  "MX-SON":26,"MX-TAB":27,"MX-TAM":28,"MX-TLA":29,"MX-VER":30,
  "MX-YUC":31,"MX-ZAC":32,
};

export const ALIAS_ESTADO = {
  "michoacan de ocampo":             "michoacan",
  "coahuila de zaragoza":            "coahuila",
  "veracruz de ignacio de la llave": "veracruz",
  "ciudad de mexico":                "distrito federal",
  "michoacan":                       "michoacan de ocampo",
  "coahuila":                        "coahuila de zaragoza",
  "veracruz":                        "veracruz de ignacio de la llave",
  "distrito federal":                "ciudad de mexico",
};

// ══════════════════════════════════════════════════════════════════════
// KPI CARD
// ══════════════════════════════════════════════════════════════════════
export function KpiCard({ label, value, unit, delta, deltaLabel, accentColor }) {
  const accent = accentColor || COLOR.gold;
  const pos = delta > 0;
  const neg = delta < 0;
  return (
    <div style={{
      background: COLOR.card, borderRadius: 6, padding: "12px 16px",
      border: `1px solid ${COLOR.border}`, flex: 1, minWidth: 160,
    }}>
      <div style={{
        fontSize: 13, color: COLOR.muted, fontWeight: 700,
        fontFamily: "monospace", textTransform: "uppercase",
        letterSpacing: ".6px", marginBottom: 6,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 22, fontWeight: 800, color: accent,
        lineHeight: 1, fontFamily: "monospace",
      }}>
        {value}{" "}
        <span style={{ fontSize: 15, fontWeight: 400, color: COLOR.muted }}>{unit}</span>
      </div>
      {delta != null && (
        <div style={{
          fontSize: 14, marginTop: 5, fontFamily: "monospace",
          color: pos ? COLOR.green : neg ? COLOR.red : COLOR.muted,
        }}>
          {pos ? "▲" : neg ? "▼" : "─"} {fmtPct(delta)}
          {deltaLabel && <span style={{ color: COLOR.dim }}> {deltaLabel}</span>}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// BAR ROW
// ══════════════════════════════════════════════════════════════════════
export function BarRow({ label, value, maxValue, color, rightLabel }) {
  const pct = maxValue > 0 ? Math.max(2, (value / maxValue) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <div style={{
        width: 120, fontSize: 14, color: COLOR.muted, fontFamily: "monospace",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flexShrink: 0,
      }}>
        {label}
      </div>
      <div style={{ flex: 1, height: 6, background: COLOR.border, borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color || COLOR.gold, borderRadius: 4 }} />
      </div>
      <div style={{
        fontSize: 14, color: COLOR.muted, width: 70,
        fontFamily: "monospace", textAlign: "right", flexShrink: 0,
      }}>
        {rightLabel}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECTION HEADER
// ══════════════════════════════════════════════════════════════════════
export function SectionHeader({ icon, title, subtitle, badge, badgeColor }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 16px", borderBottom: `1px solid ${COLOR.border}`,
    }}>
      <span style={{ fontSize: 16, color: COLOR.gold }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{
          fontWeight: 600, fontSize: 16, color: COLOR.text,
          fontFamily: "monospace", letterSpacing: "0.03em",
        }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 13, color: COLOR.muted, marginTop: 1 }}>{subtitle}</div>
        )}
      </div>
      {badge && (
        <span style={{
          fontSize: 12, padding: "2px 8px", borderRadius: 4, fontWeight: 700,
          fontFamily: "monospace", letterSpacing: "0.04em",
          background: badgeColor?.bg || `${COLOR.gold}22`,
          color:      badgeColor?.text || COLOR.gold,
          border: `1px solid ${COLOR.goldDim || COLOR.gold + "44"}`,
        }}>
          {badge}
        </span>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// INSIGHT BOX (narrativa LLM)
// ══════════════════════════════════════════════════════════════════════
export function InsightBox({ text }) {
  if (!text) return null;
  const clean = stripMarkdown(text);
  const PRIO_MARKER = "prioridades de revisi";
  const lc       = clean.toLowerCase();
  const prioIdx  = lc.indexOf(PRIO_MARKER);
  const narrativa   = prioIdx >= 0 ? clean.slice(0, prioIdx).trim() : clean;
  const prioridades = prioIdx >= 0 ? clean.slice(prioIdx).trim() : null;
  const parrafos    = narrativa.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  const prioItems   = prioridades
    ? prioridades
        .replace(/^[^:]+:\s*/i, "")
        .split(/\n\d+\.\s+/)
        .map(p => p.trim())
        .filter(Boolean)
    : [];

  return (
    <div style={{
      background: `${COLOR.gold}0d`, border: `1px solid ${COLOR.goldDim || COLOR.gold + "44"}`,
      borderRadius: 6, padding: "12px 16px", fontSize: 15, lineHeight: 1.75,
      color: COLOR.text, marginTop: 10,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 700, fontFamily: "monospace", letterSpacing: "0.05em",
        background: `${COLOR.gold}22`, color: COLOR.gold,
        display: "inline-block", padding: "1px 8px", borderRadius: 4, marginBottom: 10,
        border: `1px solid ${COLOR.goldDim || COLOR.gold + "44"}`,
      }}>
        ✦ Comentario analítico
      </div>
      {parrafos.map((p, i) => (
        <p key={i} style={{ margin: "0 0 10px 0" }}>{p}</p>
      ))}
      {prioItems.length > 0 && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${COLOR.goldDim || COLOR.gold + "22"}`, paddingTop: 10 }}>
          <div style={{
            fontSize: 12, fontWeight: 700, fontFamily: "monospace",
            letterSpacing: "0.05em", color: COLOR.gold, marginBottom: 8, textTransform: "uppercase",
          }}>
            Prioridades de revisión
          </div>
          {prioItems.map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-start" }}>
              <span style={{
                flexShrink: 0, width: 18, height: 18, borderRadius: "50%",
                background: `${COLOR.gold}22`, border: `1px solid ${COLOR.goldDim || COLOR.gold + "44"}`,
                fontSize: 12, fontWeight: 700, color: COLOR.gold,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "monospace", marginTop: 2,
              }}>{i + 1}</span>
              <span style={{ fontSize: 14, lineHeight: 1.6, color: COLOR.muted }}>{item}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAPA LEYENDA
// ══════════════════════════════════════════════════════════════════════
export function MapaLeyenda() {
  const pasos = [
    { color: "#1e3a8a" }, { color: "#0369a1" }, { color: "#0891b2" },
    { color: "#0d9488" }, { color: "#16a34a" }, { color: "#ca8a04" },
    { color: "#ea580c" }, { color: "#dc2626" },
  ];
  const gradient = pasos.map(p => p.color).join(", ");
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
      <span style={{ fontSize: 12, color: COLOR.muted, fontFamily: "monospace" }}>Bajo</span>
      <div style={{
        width: 140, height: 10, borderRadius: 4,
        background: `linear-gradient(to right, ${gradient})`, flexShrink: 0,
      }} />
      <span style={{ fontSize: 12, color: COLOR.muted, fontFamily: "monospace" }}>Máximo</span>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 8 }}>
        <div style={{
          width: 10, height: 10, borderRadius: 2, background: "#252836",
          opacity: 0.5, border: "1px solid #2a2f40", flexShrink: 0,
        }} />
        <span style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace" }}>Sin reporte</span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ESCALA DE COLOR CUANTIL (compartida por ambos mapas)
// ══════════════════════════════════════════════════════════════════════
function useColorScale(data, metrica) {
  const sortedVals = React.useMemo(() => {
    if (!data?.length) return [];
    return data.map(d => d[metrica] || 0).filter(v => v > 0).sort((a, b) => a - b);
  }, [data, metrica]);

  const colorScale = useCallback((val) => {
    if (!val || val <= 0 || !sortedVals.length) return "#1a2535";
    let lo = 0, hi = sortedVals.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sortedVals[mid] < val) lo = mid + 1; else hi = mid;
    }
    const pct = sortedVals.length > 1 ? lo / (sortedVals.length - 1) : 1;
    if (pct < 0.08) return "#1e3a8a";
    if (pct < 0.20) return "#0369a1";
    if (pct < 0.34) return "#0891b2";
    if (pct < 0.50) return "#0d9488";
    if (pct < 0.65) return "#16a34a";
    if (pct < 0.80) return "#ca8a04";
    if (pct < 0.92) return "#ea580c";
    return "#dc2626";
  }, [sortedVals]);

  return colorScale;
}

// ══════════════════════════════════════════════════════════════════════
// MAPA DE ESTADOS (coroplético)
// Props:
//   data      — array de objetos con campo 'nombre_estado' y métricas
//   metrica   — clave del campo numérico a colorear
//   tooltipFn — fn(d) → string HTML para el tooltip (opcional)
// ══════════════════════════════════════════════════════════════════════
export function MapaEstados({ data, metrica, tooltipFn }) {
  const mapRef     = useRef(null);
  const leafletRef = useRef(null);
  const geoRef     = useRef(null);
  const [geojson, setGeojson] = useState(null);

  useEffect(() => {
    fetchGeoEstados().then(setGeojson).catch(e => console.warn("GeoJSON estados:", e));
  }, []);

  const dataIdx = React.useMemo(() => {
    if (!data) return { byCode: {}, byName: {} };
    const byCode = Object.fromEntries(
      (data || []).filter(d => d.cve_estado).map(d => [d.cve_estado, d])
    );
    const byName = {};
    (data || []).forEach(d => {
      const key = normalizeStr(d.nombre_estado || d.estado || "");
      byName[key] = d;
      const alt = ALIAS_ESTADO[key];
      if (alt) byName[alt] = d;
    });
    return { byCode, byName };
  }, [data]);

  const colorScale = useColorScale(data, metrica);

  const lookupFeature = useCallback((feature) => {
    let cve = parseInt(feature.properties?.CVE_ENT || feature.properties?.cve_ent || 0);
    if (!(cve > 0)) {
      const mxId = feature.properties?.id || feature.properties?.ID || "";
      cve = MX_ISO_TO_CVE[mxId] || 0;
    }
    if (cve > 0 && dataIdx.byCode[cve]) return dataIdx.byCode[cve];
    const rawName = normalizeStr(
      feature.properties?.ESTADO || feature.properties?.estado ||
      feature.properties?.name   || feature.properties?.NAME || ""
    );
    return dataIdx.byName[rawName] || dataIdx.byName[ALIAS_ESTADO[rawName]] || null;
  }, [dataIdx]);

  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    if (leafletRef.current) { leafletRef.current.invalidateSize(); return; }
    leafletRef.current = window.L.map(mapRef.current, {
      center: [23.8, -102.3], zoom: 5, zoomControl: true,
      attributionControl: false, scrollWheelZoom: true,
      maxBounds: [[13.5, -120.0], [33.5, -85.0]], maxBoundsViscosity: 0.8,
    });
    setTimeout(() => leafletRef.current?.invalidateSize(), 50);
    setTimeout(() => leafletRef.current?.invalidateSize(), 300);
  }, []);

  useEffect(() => {
    const L = window.L;
    if (!L || !leafletRef.current || !geojson || !data) return;
    if (geoRef.current) { geoRef.current.remove(); geoRef.current = null; }

    geoRef.current = L.geoJSON(geojson, {
      style: (feature) => {
        const d   = lookupFeature(feature);
        const val = d?.[metrica] || 0;
        return { fillColor: colorScale(val), fillOpacity: 0.92, color: "#070c12", weight: 0.8 };
      },
      onEachFeature: (feature, layer) => {
        const d      = lookupFeature(feature);
        const nombre = d?.nombre_estado || d?.estado ||
          feature.properties?.ESTADO || feature.properties?.name || "—";
        const tooltipHtml = tooltipFn
          ? tooltipFn(d, nombre)
          : `<div style="font-family:monospace;padding:6px 8px;min-width:150px;background:#0d1520;border:1px solid #1a2535;border-radius:4px">
               <div style="font-size:12px;font-weight:700;color:#dde4ee;margin-bottom:4px">${nombre}</div>
               <div style="font-size:10px;color:#c8a96e">${metrica}: ${d?.[metrica] ?? "—"}</div>
             </div>`;
        layer.bindTooltip(tooltipHtml, { sticky: true, opacity: 1, className: "leaflet-tooltip-emrisk" });
        layer.on("mouseover", () => layer.setStyle({ weight: 2, color: "#c8a96e", fillOpacity: 1 }));
        layer.on("mouseout",  () => geoRef.current?.resetStyle(layer));
      },
    }).addTo(leafletRef.current);

    try {
      leafletRef.current.fitBounds(geoRef.current.getBounds(), { padding: [12, 12], maxZoom: 7 });
    } catch (_) {}
    leafletRef.current.invalidateSize();
    setTimeout(() => leafletRef.current?.invalidateSize(), 150);
  }, [geojson, dataIdx, metrica, colorScale, lookupFeature, data, tooltipFn]);

  return <div ref={mapRef} style={{ height: "100%", width: "100%", background: "#070c12" }} />;
}

// ══════════════════════════════════════════════════════════════════════
// MAPA DE MUNICIPIOS
// Props:
//   data      — array con campos 'cvegeo' (5 dígitos) y métricas
//   metrica   — clave del campo numérico a colorear
//   tooltipFn — fn(d, nombre) → string HTML (opcional)
// ══════════════════════════════════════════════════════════════════════
export function MapaMunicipios({ data, metrica, tooltipFn }) {
  const mapRef     = useRef(null);
  const leafletRef = useRef(null);
  const geoRef     = useRef(null);
  const [geojson, setGeojson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchGeoMunicipios()
      .then(d => { setGeojson(d); setLoading(false); })
      .catch(e => { console.warn("GeoJSON municipios:", e); setLoading(false); });
  }, []);

  const dataIdx = React.useMemo(() => {
    if (!data) return {};
    return Object.fromEntries(data.map(d => [d.cvegeo, d]));
  }, [data]);

  const colorScale = useColorScale(data, metrica);

  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    if (leafletRef.current) { leafletRef.current.invalidateSize(); return; }
    leafletRef.current = window.L.map(mapRef.current, {
      center: [23.8, -102.3], zoom: 5, zoomControl: true,
      attributionControl: false, scrollWheelZoom: true,
      maxBounds: [[13.5, -120.0], [33.5, -85.0]], maxBoundsViscosity: 0.8,
    });
    setTimeout(() => leafletRef.current?.invalidateSize(), 50);
    setTimeout(() => leafletRef.current?.invalidateSize(), 300);
  }, []);

  useEffect(() => {
    const L = window.L;
    if (!L || !leafletRef.current || !geojson || !data) return;
    if (geoRef.current) { geoRef.current.remove(); geoRef.current = null; }

    geoRef.current = L.geoJSON(geojson, {
      style: (feature) => {
        const cvegeo = feature.properties?.CVEGEO || feature.properties?.cvegeo || "";
        const d      = dataIdx[cvegeo];
        const val    = d?.[metrica] || 0;
        return { fillColor: colorScale(val), fillOpacity: 0.9, color: "#070c12", weight: 0.3 };
      },
      onEachFeature: (feature, layer) => {
        const cvegeo = feature.properties?.CVEGEO || feature.properties?.cvegeo || "";
        const d      = dataIdx[cvegeo];
        const nombre = d?.nombre_municipio || d?.municipio ||
          feature.properties?.NOM_MUN || feature.properties?.NOMGEO || cvegeo;
        const estado = d?.nombre_estado || d?.estado || feature.properties?.NOM_ENT || "";
        const tooltipHtml = tooltipFn
          ? tooltipFn(d, nombre, estado)
          : `<div style="font-family:monospace;padding:6px 8px;min-width:150px;background:#0d1520;border:1px solid #1a2535;border-radius:4px">
               <div style="font-size:11px;font-weight:700;color:#dde4ee;margin-bottom:2px">${nombre}</div>
               <div style="font-size:10px;color:#8aa4b8;margin-bottom:4px">${estado}</div>
               <div style="font-size:10px;color:#c8a96e">${metrica}: ${d?.[metrica] ?? "—"}</div>
             </div>`;
        layer.bindTooltip(tooltipHtml, { sticky: true, opacity: 1, className: "leaflet-tooltip-emrisk" });
        layer.on("mouseover", () => layer.setStyle({ weight: 1.5, color: "#c8a96e", fillOpacity: 1 }));
        layer.on("mouseout",  () => geoRef.current?.resetStyle(layer));
      },
    }).addTo(leafletRef.current);

    try {
      leafletRef.current.fitBounds(geoRef.current.getBounds(), { padding: [10, 10], maxZoom: 7 });
    } catch (_) {}
    leafletRef.current.invalidateSize();
    setTimeout(() => leafletRef.current?.invalidateSize(), 150);
  }, [geojson, dataIdx, metrica, colorScale, data, tooltipFn]);

  if (loading) return (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
                  background: "#070c12", color: COLOR.muted, fontSize: 14, fontFamily: "monospace" }}>
      Cargando mapa de municipios…
    </div>
  );

  return <div ref={mapRef} style={{ height: "100%", width: "100%", background: "#070c12" }} />;
}
